import {
  Button,
  ChakraStyledOptions,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  InvoiceDisbursementFragmentGQLType,
  InvoiceDisbursementGQLType,
  InvoiceReportGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { DisbursementNoticeDownloadButton } from '~/packages/invoices/ui/disbursements/InvoiceDisbursementDownloadButton';
import { MarkAsDisbursedButton } from '~/packages/invoices/ui/disbursements/MarkAsDisbursedButton';
import { DownloadAllLiveDisbursementNoticesButton } from '~/packages/loans/ui/DownloadAllLiveDisbursementNoticesButton';

const TABLE_CELL_STYLES = {
  backgroundColor: 'white',
  color: colors.gray[500],
  whiteSpace: 'normal',
};

const HeaderCell = ({
  styles,
  children,
}: {
  styles?: ChakraStyledOptions;
  children: React.ReactNode;
}) => {
  return (
    <Th fontWeight={500} fontSize="14px" color="black" {...styles}>
      {children}
    </Th>
  );
};

const InvoiceDisbursementRow = ({
  disbursement,
  hideBorder,
  reportDownload,
}: {
  disbursement: InvoiceDisbursementGQLType;
  hideBorder: boolean;
  reportDownload: InvoiceReportGQLType | undefined;
}) => {
  return (
    <Tr key={disbursement.id}>
      <Td sx={TABLE_CELL_STYLES} border={hideBorder ? 'none' : undefined}>
        {disbursement.status === 'paid' ? (
          <Icon icon="check" color={colors.success} />
        ) : (
          <MarkAsDisbursedButton invoiceDisbursement={disbursement} />
        )}
      </Td>
      <Td
        sx={{ fontWeight: 500, color: 'black' }}
        border={hideBorder ? 'none' : undefined}
      >
        {disbursement.capitalProviderOrganization.name}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
          textAlign: 'right',
        }}
      >
        {disbursement.capitalProviderProRata
          ? percentageToPercentageLabel(
              disbursement.capitalProviderProRata * 100,
              6,
              true,
            )
          : '-'}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(disbursement.totalAmount)}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {disbursement.paymentDate
          ? commonDate.fullDateStrToHumanDateString(disbursement.paymentDate)
          : '-'}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
          textAlign: 'center',
        }}
      >
        {reportDownload ? (
          <DisbursementNoticeDownloadButton report={reportDownload} />
        ) : (
          <Tooltip label="No Funding Disbursement Notice Found">
            <Button variant="v2-ghost" isDisabled>
              <Icon icon="arrow-down-to-bracket" />
            </Button>
          </Tooltip>
        )}
      </Td>
    </Tr>
  );
};

export const InvoiceDisbursementsTable = ({
  disbursements,
  invoiceReports,
  creditFacilityId,
}: {
  disbursements: InvoiceDisbursementFragmentGQLType[];
  invoiceReports?: InvoiceReportGQLType[];
  creditFacilityId: string;
}) => {
  return (
    <Table>
      <Thead>
        <Tr>
          <HeaderCell styles={{ width: '15%' }}>Disbursed</HeaderCell>
          <HeaderCell styles={{ width: '20%' }}>Lender</HeaderCell>
          <HeaderCell
            styles={{
              textAlign: 'right',
              width: '20%',
            }}
          >
            Lender Share
          </HeaderCell>
          <HeaderCell styles={{ width: '20%', textAlign: 'right' }}>
            Funds Owed to Lender
          </HeaderCell>
          <HeaderCell styles={{ width: '20%' }}>Disbursement Date</HeaderCell>
          <Th
            fontWeight={500}
            fontSize="14px"
            color="black"
            width="5%"
            textAlign="center"
          >
            <Menu>
              <MenuButton
                fontSize="inherit"
                fontWeight="inherit"
                aria-label="Download and email all funding requests"
              >
                <HStack>
                  <span>Notice</span>
                  <Icon icon="chevron-down" />
                </HStack>
              </MenuButton>
              <MenuList>
                <MenuItem as="div">
                  <DownloadAllLiveDisbursementNoticesButton
                    invoiceReports={invoiceReports}
                    creditFacilityId={creditFacilityId}
                  />
                </MenuItem>
              </MenuList>
            </Menu>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {disbursements.map((disbursement, idx) => {
          // hide the bottom border on the last row
          const hideBorder = idx === disbursements.length - 1;
          const reportDownload = invoiceReports?.find(
            (download) =>
              download.capitalProviderId ===
                disbursement.capitalProviderOrganization.id &&
              download.reportType === 'disbursementNotice',
          );

          return (
            <InvoiceDisbursementRow
              key={disbursement.id}
              disbursement={disbursement}
              hideBorder={hideBorder}
              reportDownload={reportDownload}
            />
          );
        })}
      </Tbody>
    </Table>
  );
};
